import { useCallback, useContext, useMemo } from "react";
import useCartBookingCompleteMutation from "../../hooks/mutations/useCartBookingCompleteMutation";
import useCartDeliveryCompleteMutation from "../../hooks/mutations/useCartDeliveryCompleteMutation";
import { updateCartRequest } from "../../hooks/mutations/useCartRequestUpdateMutation";
import CartContext from "../../context/cart";
import { useCartBookingCreateWithErrorMutation } from "../../hooks/mutations/useCartBookingCreateMutation";
import UserContext from "../../context/user";

const CartBookingForm = ({ tempCartToSubmit, tempBookingDate }) => {
  const {
    labels,
    campaign,
    formData,
    partial,
    survey,
    cartRequest,
    setCartRequest,
    setPartial,
    setFormData,
    setCurrentSectionIndex,
    setSlotNotAvailable,
  } = useContext(CartContext);

  const { callApi } = useContext(UserContext);

  const reservationQuestionId = useMemo(
    () =>
      survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Reservation")
        ?.id,
    [survey]
  );
  const section = useMemo(() => survey?.definition?.sections?.[3], [survey]);

  const { mutateAsync: completeCartBooking } = useCartBookingCompleteMutation();
  const { mutateAsync: completeCartDelivery, isError } = useCartDeliveryCompleteMutation();
  const pickUpPointQuestionId = useMemo(
    () =>
      survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Number")?.id,
    [survey]
  );
  const { mutateAsync: createCartBooking } = useCartBookingCreateWithErrorMutation();
  const pickUpPointId = useMemo(() => {
    const pickUpPointQuestionId = survey?.definition?.sections?.[4]?.questions?.find(
      (q) => q.questionType === "Number"
    )?.id;
    return formData?.[pickUpPointQuestionId];
  }, [formData, survey]);

  const handleSubmit = useCallback(async () => {
    if (tempCartToSubmit || tempBookingDate) {
      const { reservationId, pickupPointId } = await createCartBooking({
        pickUpPointId,
        ...tempBookingDate,
      });
      const newValues = {
        ...formData,
        ...(tempCartToSubmit?.values ?? {}),
        [pickUpPointQuestionId]: pickupPointId,
        [reservationQuestionId]: reservationId,
      };
      const response = await updateCartRequest(
        {
          campaignId: campaign?.id,
          cartRequest,
          survey,
          formData,
          partial,
          email:
            tempCartToSubmit?.formData?.[section.questions[3].id] ??
            formData?.[section.questions[3].id],
          phone:
            tempCartToSubmit?.formData?.[section.questions[2].id] ??
            formData?.[section.questions[2].id],
          ...(tempCartToSubmit ?? {}),
          values: newValues,
          confirmationStep: true,
          pickupPointId: pickupPointId,
        },
        callApi
      );
      setCartRequest(response);
      setPartial(response.surveySubmission.submissionId);
      setFormData(newValues);
      setCurrentSectionIndex((current) => current + 1);
      setSlotNotAvailable(false);
      return;
    }

    if (cartRequest?.deliveryOption === "INDIRIZZO") {
      await completeCartDelivery({
        cartUserRequestId: cartRequest?.id,
      });
    } else {
      await completeCartBooking({
        campaignId: campaign?.id,
        answerId: partial,
        pickUpPointId:
          formData[
            survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Number")
              ?.id
          ],
        reservationId: cartRequest.reservationId,
      });
    }
  }, [
    tempCartToSubmit,
    tempBookingDate,
    cartRequest,
    createCartBooking,
    pickUpPointId,
    formData,
    pickUpPointQuestionId,
    reservationQuestionId,
    campaign?.id,
    survey,
    partial,
    section.questions,
    callApi,
    setCartRequest,
    setPartial,
    setFormData,
    setCurrentSectionIndex,
    setSlotNotAvailable,
    completeCartDelivery,
    completeCartBooking,
  ]);

  return (
    <div className="form__buttons-wrapper">
      <div className="form__buttons">
        <button className="button primary form__submit" onClick={handleSubmit}>
          {labels?.WELFARE_FORM_BTN_CONFIRM || "Confirm"}
        </button>
      </div>
      {isError && (
        <div className="error">
          {labels?.WELFARE_ERROR_GENERIC || "An unexpected error occurred"}
        </div>
      )}
    </div>
  );
};

export default CartBookingForm;
